import Axios, { AxiosError, AxiosResponse } from 'axios'
import storage from 'utils/storage'
import { queryClient } from './react-query'
import {
  API_URL_DEV,
  API_URL_SANDBOX,
  API_URL_UAT,
  API_URL_PROD,
} from './constants'
import { logout, setOnline, store } from './user/store'
import { setPermission } from './user/store/permissionSlice'
// import { setNetworkError } from './networkErrorStore';

const url = new URL(window.location.href)
const hostname = url.hostname
const parts = hostname.split('-')
const environmentMode = parts[0]
// const environmentMode = 'dev'

const getApiUrl = (environmentMode: string): string => {
  switch (environmentMode) {
    case 'localhost':
      return API_URL_DEV || ''
    case 'dev':
      return API_URL_DEV || ''
    case 'sandbox':
      return API_URL_SANDBOX || ''
    case 'uat':
      return API_URL_UAT || ''
    default:
      return API_URL_PROD || ''
  }
}

export interface ENV_VARIABLES {
  api_base_path: string
  api_load_balancer_path: string
  socket_bath_path: string
  socket_handshake_path: string
}

const getEnv = (environmentMode: string): ENV_VARIABLES => {
  switch (environmentMode) {
    case 'localhost':
      return {
        api_base_path: 'https://connect.krank.com/dev/configurator-api/',
        api_load_balancer_path:
          'https://stg-app.krank.com/dev/configurator/api/v1/',
        socket_bath_path: 'wss://stg-app.krank.com',
        socket_handshake_path: '/dev/configurator/api/v1/socket',
      }
    case 'dev':
      return {
        api_base_path: 'https://connect.krank.com/dev/configurator-api/',
        api_load_balancer_path:
          'https://stg-app.krank.com/dev/configurator/api/v1/',
        socket_bath_path: 'wss://stg-app.krank.com',
        socket_handshake_path: '/dev/configurator/api/v1/socket',
      }
    case 'sandbox':
      return {
        api_base_path: 'https://connect.krank.com/sandbox/configurator-api/',
        api_load_balancer_path:
          'https://stg-app.krank.com/uat/configurator/api/v1/',
        socket_bath_path: 'wss://stg-app.krank.com',
        socket_handshake_path: '/sandbox/configurator/api/v1/socket',
      }
    case 'uat':
      return {
        api_base_path: 'https://connect.krank.com/uat/configurator-api/',
        api_load_balancer_path:
          'https://stg-app.krank.com/uat/configurator/api/v1/',
        socket_bath_path: 'wss://stg-app.krank.com',
        socket_handshake_path: '/uat/configurator/api/v1/socket',
      }
    default:
      return {
        api_base_path: 'https://connect.krank.com/prod/configurator-api/',
        api_load_balancer_path:
          'https://app.krank.com/prod/configurator/api/v1/',
        socket_bath_path: 'wss://app.krank.com',
        socket_handshake_path: '/prod/configurator/api/v1/socket',
      }
  }
}

export const apiUrl = getApiUrl(environmentMode)
export const envVariables = getEnv(environmentMode)
// const lang = store.getState().locale.lang

const axios = Axios.create({
  baseURL: envVariables.api_base_path,
  headers: {
    'Content-Type': 'application/json',
    'Accept': '/',
    // 'x-locale': lang,
  },
})

const setLang = (newLang: string) => {
  axios.defaults.headers.common['x-locale'] = newLang
}

store.subscribe(() => {
  const lang = store.getState().locale.lang
  setLang(lang)
})

const setAccessToken = (token: string) => {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

axios.interceptors.request.use(config => {
  const token = storage.getToken()

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
    setAccessToken(token)
  }

  const lang = store.getState().locale.lang
  config.headers['x-locale'] = lang
  return config
})

axios.interceptors.response.use(
  (response: AxiosResponse) => {
    const accessToken = response.data['Authorization']
    const status = response?.data?.statusCode
    if (accessToken) {
      setAccessToken(accessToken)
    }

    if (status === 401) {
      storage.clearToken()
      queryClient.clear()
      store.dispatch(logout())
      return response.data
    }
    if (!store.getState().networkError.online) {
      store.dispatch(setOnline(true))
    }
    if (status === 403) {
      const msg = response?.data?.message
      store.dispatch(setPermission({ denied: true, message: msg }))
    }

    if (response?.data?.statusCode < 400) {
      return response.data
    } else {
      throw response.data
    }
  },
  (error: AxiosError) => {
    const status = error.response?.status
    if (error.message === 'Network Error' && !navigator.onLine) {
      store.dispatch(setOnline(false))
    }
    if (!error.response || (status && status >= 500)) {
      if (store.getState().networkError.online) {
        store.dispatch(setOnline(false))
      }
    }
    return Promise.reject(error)
  }
)

export default axios
